<template>
  <div class="project-list-container">
    <h1 class="itemis-analyze-headline mb-2 pb-10">
      {{ labels.HOMEPAGE.DETAILS.PROJECTS }}
    </h1>
    <div id="project_list" fluid class="d-flex">
      <v-container id="project_list" fluid class="mb-4 pt-0 d-flex pl-2">
        <v-row no-gutters class="d-flex align-center" justify="space-between">
          <v-col cols="3" sm="6" md="4" class="pr-5">
            <analyze-search
              id="search-field"
              v-model="search"
              data-testid="project-search"
              @click:clear="search = ''"
            />
          </v-col>
          <v-col>
            <v-btn
              data-testid="new-project-btn"
              variant="flat"
              color="cyan-dark"
              class="new-project-button"
              :prepend-icon="CONSTANTS.ICONS_MDI.ICON_PLUS"
              @click="openDialog"
            >
              {{ labels.BUTTONS.NEW_PROJECT }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </div>
    <div id="listContainer" class="list-container">
      <analyze-data-table
        table-id="projects-table-id"
        :headers="projectHeaders"
        :fixed-header="true"
        :fixed-footer="true"
        :max-height="70"
        :items="projectItems"
        :has-delete-action="true"
        :has-favorites="true"
        :sort-by="sortBy"
        :has-multisort="true"
        :search="search"
        :items-per-page="25"
        :total-items="projectItems.length"
        data-type="projects"
        @select="selectProject"
        @delete="deleteProject"
        @favorite="toggleProjectAsFavorite"
      />
    </div>
    <ConfirmDeletionDialog
      v-if="selectedProjectToDelete"
      :key="`dialog-${selectedProjectToDelete?.id}`"
      :element-to-delete="selectedProjectToDelete"
      :get-element-name="(p?: Project) => p?.name ?? 'My Project'"
      :element-type-name="labels.DELETE_ELEMENT.PROJECT"
      :is-deleting-element="isDeletingProject"
      :extra-confirmation-required="true"
      @confirm-deletion="removeProject"
      @cancel-deletion="selectedProjectToDelete = undefined"
    />
    <NewProjectDialog />
  </div>
</template>

<script lang="ts" setup>
  import * as HELPERS from '@/utils/helpers'
  import labels from '@/translations/labels.json'
  import { EventBus } from '@/event-bus'
  import { Project } from '@/models/project.model'
  import ConfirmDeletionDialog from '@/components/ConfirmDeletionDialog/ConfirmDeletionDialog.vue'
  import AnalyzeSearch from '@/components/Inputs/AnalyzeSearch.vue'
  import NewProjectDialog from '@/components/NewProjectDialog/NewProjectDialog.vue'
  import notifMessage from '@/translations/notifMessage.json'
  import CONSTANTS from '@/constants/Config.constants'
  import { computed, onBeforeMount, ref } from 'vue'
  import { useProjectStore } from '@/stores/projectStore'
  import { useSnapshotStore } from '@/stores/snapshotStore'
  import { useRouter } from 'vue-router'
  import { useEventBus } from '@/utils/events'
  import { useDashboardStore } from '@/stores/dashboardStore'
  import { useGlobalFilterStore } from '@/stores/globalFilterStore'
  import { AnalyzeTableHeader, AnalyzeTableItem, SortItem } from '@/models/table.model'
  import AnalyzeDataTable from '@/components/AnalyzeDataTable/AnalyzeDataTable.vue'
  import { useCustomMetricStore } from '@/stores/customMetricStore'
  import { injectTyped } from '@/inject'
  import { ServiceKeys } from '@/services/symbols'

  const projectStore = useProjectStore()
  const snapshotStore = useSnapshotStore()
  const dashboardStore = useDashboardStore()
  const globalFiltersStore = useGlobalFilterStore()
  const customMetricStore = useCustomMetricStore()

  const router = useRouter()

  const selectedProject = ref<Project | undefined>()
  const selectedProjectToDelete = ref<Project | undefined>()
  const isLoading = ref(false)
  const isDeletingProject = ref(false)
  const search = ref<string>('')

  const projectService = injectTyped(ServiceKeys.project)
  const snapshotService = injectTyped(ServiceKeys.snapshot)

  if (!snapshotService || !projectService) {
    throw Error(notifMessage.ERROR_ALERTS.SOMETHING_WENT_WRONG)
  }

  useEventBus('loggedin', () => {
    projectStore.setProjects([])
    projectStore.setCurrentProject(undefined)
  })

  useEventBus('addNewProject', (project: Project) => {
    addNewProject(project)
  })

  onBeforeMount(async () => {
    await projectStore.loadProjectsFromApi(projectService)
  })

  const sortBy: SortItem[] = [
    { key: 'favorite', order: 'desc' },
    { key: 'updated', order: 'desc' },
  ]
  const projectHeaders = ref<AnalyzeTableHeader[]>([
    { title: '', key: 'favorite', width: '58' },
    { title: 'Name', key: 'name' },
    { title: 'Updated', key: 'updated' },
    { title: '', key: 'actions', align: 'end', sortable: false },
  ])

  const projectItems = computed((): AnalyzeTableItem<Project>[] =>
    projectStore.getDateSortedProjects.map((project) => ({
      name: project.name,
      updated: new Date(HELPERS.getCreationDateOnly(project.creationDate as string) || ''),
      value: project,
      favorite: !!project.favorite,
    })),
  )

  function addNewProject(newProject: Project): void {
    projectStore.addNewProject(newProject)
    HELPERS.showTopAlertMessage(true, 'success', notifMessage.SUCCESS_ALERTS.PROJECT_CREATED)
  }

  async function selectProject(project: Project): Promise<void> {
    if (project === undefined || snapshotService === undefined) {
      return
    }

    selectedProject.value = project
    isLoading.value = true
    projectStore.setCurrentProject(project)

    snapshotStore.loadSnapshotsFromApi({
      projectId: selectedProject.value.id,
    })

    customMetricStore.loadCustomMetricsFromApi(selectedProject.value.id, undefined)

    dashboardStore.$reset()
    globalFiltersStore.$reset()

    const message = HELPERS.replaceVariableInString(
      labels.ALERT_VIEW.PROJECT_SELECTED_ALERT,
      selectedProject.value.name,
    )
    HELPERS.showTopAlertMessage(true, 'info', message)

    router.push({
      name: labels.SIDEBAR.MAIN.PROJECT_CONFIGURATION,
      params: {
        projectId: selectedProject.value.id.toString(),
      },
    })
  }

  function deleteProject(project: Project): void {
    if (project) {
      selectedProjectToDelete.value = project as Project
    }
  }

  async function removeProject(project: Project): Promise<void> {
    if (!projectService) {
      throw Error(notifMessage.ERROR_ALERTS.SOMETHING_WENT_WRONG)
    }

    try {
      isDeletingProject.value = true

      await projectStore.removeProject(projectService, project)

      projectStore.setCurrentProject(undefined)
      snapshotStore.resetSnapshots()

      HELPERS.showTopAlertMessage(true, 'success', notifMessage.SUCCESS_ALERTS.PROJECT_DELETED)
    } catch {
      HELPERS.showTopAlertMessage(true, 'error', notifMessage.ERROR_ALERTS.DELETE_PROJECT_ERROR)
    } finally {
      isDeletingProject.value = false
      selectedProjectToDelete.value = undefined
    }
  }

  function openDialog(): void {
    EventBus.emit('openDialog')
  }

  async function toggleProjectAsFavorite(item: Project): Promise<void> {
    if (item === undefined) {
      return
    }
    if (!projectService) {
      throw Error(notifMessage.ERROR_ALERTS.SOMETHING_WENT_WRONG)
    }
    const currentProject = item as Project
    try {
      let data: Project
      currentProject.favorite = !currentProject.favorite

      if (currentProject.favorite) {
        data = await projectService.addProjectToFavorites(currentProject)
      } else {
        data = await projectService.removeProjectFromFavorites(currentProject)
      }

      if (data) {
        projectStore.setProjectAsFavorite(currentProject)

        if (currentProject.favorite) {
          HELPERS.showTopAlertMessage(
            true,
            'info',
            notifMessage.SUCCESS_ALERTS.PROJECT_ADDED_TO_FAVORITES,
          )
        } else {
          HELPERS.showTopAlertMessage(
            true,
            'info',
            notifMessage.SUCCESS_ALERTS.PROJECT_REMOVED_FROM_FAVORITES,
          )
        }
      } else {
        currentProject.favorite = !currentProject.favorite

        HELPERS.showTopAlertMessage(true, 'error', notifMessage.ERROR_ALERTS.ADD_FAVORITE_ERROR)
      }
    } catch (e) {
      console.error(e)
      HELPERS.showTopAlertMessage(true, 'error', notifMessage.ERROR_ALERTS.ADD_FAVORITE_ERROR)
    }
  }
</script>

<style lang="scss" scoped>
  .newProjectBtn {
    text-transform: none;
    padding-left: 19px;
  }

  .enableDelBtn {
    position: absolute;
    right: 12rem;
  }

  .delProjectBtn {
    position: absolute;
    right: 4rem;
  }
  .v-list-item--variant-text .v-list-item__overlay {
    display: none;
  }

  .list-container {
    overflow-y: hidden;
    flex-grow: 1;
    overflow-x: hidden;
  }

  .project-List-searchfield-header {
    font-size: 15px;
    color: rgb(var(--v-theme-font-color));
  }

  .table-layout {
    height: calc(110vh - 120px);
  }

  .theme--light.v-data-table {
    background-color: transparent;
  }

  #fav_column.fav_icon {
    min-width: 0%;
    padding-left: 1.3em;
  }

  #fav_column.fav_icon:hover,
  #favorite:hover {
    transform: scale(1.1, 1.1);
  }

  #favorite {
    cursor: pointer;
  }

  #favorite:hover {
    .fav-icon-outline {
      border-color: rgb(var(--v-theme-itemis-analyze-primary));
      color: rgb(var(--v-theme-itemis-analyze-primary));
    }
  }

  .columns-resize-bar {
    border-left: 5px solid rgb(var(--v-theme-surface));
    height: 2000px !important;
  }

  .fav-icon {
    border-color: rgb(var(--v-theme-itemis-analyze-primary));
    color: rgb(var(--v-theme-itemis-analyze-primary));
  }

  .fav-icon-outline {
    color: rgb(var(--v-theme-grey-dark));
  }

  .project-name {
    font-weight: bolder;
  }

  .project-list-container {
    padding-left: 100px;
    padding-right: 100px;
    padding-bottom: 40px;
    padding-top: 25px;
  }

  .new-project-button {
    margin-bottom: 20px;
  }
</style>
